import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

export default class extends BridgeComponent {
  static component = "facebook-sign-in"
  static values = { appId: String, redirectUri: String, url: String, trackUrl: String, companyId: String, errorMessage: String }
  static targets = ["error"]

  connect() {
    super.connect()
    this.send("connect", { appId: this.appIdValue, redirectUri: this.redirectUriValue })
  }

  signIn(event) {
    if (!this.enabled) return

    event.preventDefault()
    this.#hideError()

    this.send("requestSignIn", {}, (message) => {
      if (message.data.error) {
        // Bridge-level failure never reaches the server — beacon it. "cancelled"
        // is a user choice, so track it but don't surface the error message.
        this.#trackFailure(message.data.error)
        if (message.data.error !== "cancelled") this.#showError()
        return
      }

      // Native Facebook Login SDK returns a user access token; older app builds
      // returned an authorization code. The server accepts either.
      if (message.data.token) {
        this.#submit({ access_token: message.data.token })
      } else if (message.data.code) {
        this.#submit({ code: message.data.code, redirect_uri: this.redirectUriValue })
      } else {
        this.#trackFailure("no_credential")
        this.#showError()
      }
    })
  }

  async #submit(credentials) {
    // Show progress: after returning from the Facebook browser the login screen
    // is visible while the server verifies the token, which otherwise looks frozen.
    this.#setLoading(true)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          ...credentials,
          company_id: this.companyIdValue,
        }),
      })

      const data = await response.json()

      if (data.success || data.needs_email) {
        // Keep the loading state while we navigate to the destination.
        window.location.href = data.redirect_url
      } else {
        // The server reached a failure branch and already tracked it; just show.
        this.#setLoading(false)
        this.#showError()
      }
    } catch (error) {
      this.#trackFailure("network_error")
      this.#setLoading(false)
      this.#showError()
    }
  }

  // Beacon a native sign-in failure that the server never saw, so the native
  // funnel drop-off (sheet cancels, missing accounts, network) is measurable.
  #trackFailure(reason) {
    if (!this.hasTrackUrlValue) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.trackUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify({ method: "facebook", reason, company_id: this.companyIdValue }),
      keepalive: true,
    }).catch(() => {})
  }

  #setLoading(loading) {
    const button = this.element.querySelector("button")
    if (!button) return
    button.disabled = loading
    button.style.opacity = loading ? "0.6" : ""
    button.style.cursor = loading ? "wait" : ""
  }

  #showError() {
    if (this.hasErrorTarget) this.errorTarget.classList.remove("hidden")
  }

  #hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }
}
